import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  CommunicationMessageResponse,
  CommunicationOrigin,
  CommunicationStatus,
  CommunicationThreadListItemResponse,
  CompleteCommunicationHistoryRecordFilesBody,
  CorrespondenceType,
  CreateCommunicationHistoryRecordRequest,
  CommunicationThreadType as GeneratedCommunicationThreadType,
  GetCommunicationMessagesResponse,
  GetCommunicationThreadListResponse,
  ReassignCommunicationThreadClientRequest,
  RecipientReference,
  SendCommunicationRequest,
  UpdateCommunicationRecordRequest,
} from 'compliance-api/generated';
import { toast } from 'react-hot-toast';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import {
  CompliOneApiError,
  formatApiErrorMessage,
  withErrorHandling,
} from '@/modules/common/utils/error-utils.tsx';

export const COMMUNICATIONS_QUERY_KEY = 'vestra-compliance/communications';

export type CommunicationThreadType = GeneratedCommunicationThreadType;
export type CommunicationRecipientDetail = RecipientReference;
export type CommunicationThread = CommunicationThreadListItemResponse;
export interface CommunicationAttachment {
  object_key: string;
  file_name: string;
  url: string;
  content_type?: string | null;
  formatted_size?: string | null;
}

export type CommunicationMessage = CommunicationMessageResponse & {
  attachments?: CommunicationAttachment[] | null;
};
type PaginatedResponse<T> = GetCommunicationThreadListResponse & { data: T[] };

export interface CommunicationThreadFilters {
  client_id?: number;
  search_text?: string;
  type?: string;
  sender?: string;
  participant?: string;
}

export const fetchCommunicationMessages = async (
  threadId: number,
  threadType: CommunicationThreadType,
) => {
  const response = await api.v1.getCommunicationMessagesV1CommunicationsThreadIdMessagesGet({
    threadId,
    thread_type: threadType,
  });
  return response.data as GetCommunicationMessagesResponse & {
    data: CommunicationMessage[];
  };
};

export interface SendCommunicationPayload extends Omit<SendCommunicationRequest, 'client_id'> {
  client_id?: number;
  optimistic_recipients?: string[];
  optimistic_recipient_details?: CommunicationRecipientDetail[];
}

export interface CreateCommunicationHistoryRecordPayload
  extends Omit<CreateCommunicationHistoryRecordRequest, 'files'> {
  optimistic_recipients?: string[];
  optimistic_recipient_details?: CommunicationRecipientDetail[];
  files?: File[];
}

export interface UpdateCommunicationRecordPayload
  extends Omit<UpdateCommunicationRecordRequest, 'files' | 'correspondence_id'> {
  correspondence_id: number;
  optimistic_thread_id?: number;
  optimistic_thread_type?: CommunicationThreadType;
  retained_storage_item_ids?: string[];
  files?: File[];
}

export interface DeleteCommunicationRecordPayload {
  correspondence_id: number;
  optimistic_thread_id?: number;
  optimistic_thread_type?: CommunicationThreadType;
}

interface CommunicationHistoryRecordFileInitItem {
  file_name: string;
  mime_type: string;
  file_size: number;
}

interface CommunicationHistoryRecordFileInitResponse {
  storage_item_id: string;
  presigned_upload_url: string;
  presigned_upload_fields?: Record<string, string> | null;
  expires_in: number;
}

interface PersistedHistoryRecordResult<T> {
  data: T;
  attachments_incomplete: boolean;
}

type InfiniteCommunicationsData = {
  pages: PaginatedResponse<CommunicationThread>[];
  pageParams: unknown[];
};

const isInfiniteCommunicationsData = (value: unknown): value is InfiniteCommunicationsData => {
  if (!value || typeof value !== 'object') return false;
  return Array.isArray((value as InfiniteCommunicationsData).pages);
};

const MIN_PENDING_VISIBILITY_MS = 1200;
const SUCCESS_REFETCH_DELAY_MS = 5000;
const normalize = (value?: string | null) => (value || '').trim().toLowerCase();

const fileToHistoryRecordInitItem = (file: File): CommunicationHistoryRecordFileInitItem => ({
  file_name: file.name,
  mime_type: file.type || 'application/octet-stream',
  file_size: file.size,
});

const uploadCommunicationHistoryRecordFiles = async (
  uploads: CommunicationHistoryRecordFileInitResponse[],
  files: File[],
) => {
  const storageItemIds = await Promise.all(
    uploads.map(async (upload, index) => {
      const file = files[index];
      if (!file) return undefined;

      const formData = new FormData();
      Object.entries(upload.presigned_upload_fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const response = await fetch(upload.presigned_upload_url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      return upload.storage_item_id;
    }),
  );

  return storageItemIds.filter(Boolean) as string[];
};

const getAttachmentPartialFailureMessage = (action: 'saved' | 'updated') =>
  `Record ${action}, but attachments were not fully uploaded. You can reopen the record and try again.`;

const getThreadFilterType = (thread: CommunicationThread): string => {
  if (thread.thread_type === GeneratedCommunicationThreadType.External) return 'external';
  if (thread.communication_origin === CommunicationOrigin.PlatformNotification) {
    return 'platform_notification';
  }
  if (thread.correspondence_type === CorrespondenceType.PhysicalMail) return 'physical_mail';
  if (thread.correspondence_type === CorrespondenceType.PhoneCall) return 'phone_call';
  if (thread.correspondence_type === CorrespondenceType.LiveMeeting) return 'live_meeting';
  return 'email';
};

const getThreadRecipientLabels = (thread: CommunicationThread) =>
  thread.recipient_details?.length
    ? thread.recipient_details.map(item => item.label)
    : thread.recipients || [];

const threadMatchesFilters = (
  thread: CommunicationThread,
  filters?: CommunicationThreadFilters,
) => {
  if (!filters) return true;

  if (filters.client_id) {
    const clientIds = thread.client_ids || [];
    if (thread.client_id !== filters.client_id && !clientIds.includes(filters.client_id)) {
      return false;
    }
  }

  if (filters.type && getThreadFilterType(thread) !== filters.type) {
    return false;
  }

  if (filters.search_text) {
    const haystack = [
      thread.subject,
      thread.preview,
      thread.sender_name,
      thread.sender_email,
      ...getThreadRecipientLabels(thread),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(normalize(filters.search_text))) {
      return false;
    }
  }

  if (filters.sender) {
    const senderHaystack = [thread.sender_name, thread.sender_email]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!senderHaystack.includes(normalize(filters.sender))) {
      return false;
    }
  }

  if (filters.participant) {
    const participantHaystack = getThreadRecipientLabels(thread).join(' ').toLowerCase();
    if (!participantHaystack.includes(normalize(filters.participant))) {
      return false;
    }
  }

  return true;
};

const buildOptimisticThread = ({
  subject,
  preview,
  recipients,
  recipientDetails,
  status,
  communicationOrigin,
  clientId,
  clientIds,
  correspondenceType,
  senderName,
  senderEmail,
  createdAt,
}: {
  subject: string;
  preview: string;
  recipients?: string[];
  recipientDetails?: CommunicationRecipientDetail[];
  status: CommunicationStatus;
  communicationOrigin: CommunicationOrigin;
  clientId?: number;
  clientIds?: number[];
  correspondenceType: CorrespondenceType;
  senderName?: string;
  senderEmail?: string;
  createdAt?: string;
}): CommunicationThread => {
  const now = new Date().toISOString();
  const tempId = -Date.now();

  return {
    id: tempId,
    thread_id: tempId,
    thread_type: GeneratedCommunicationThreadType.Communication,
    communication_origin: communicationOrigin,
    correspondence_type: correspondenceType,
    client_id: clientId,
    client_ids: clientIds,
    subject,
    preview,
    sender_name: senderName,
    sender_email: senderEmail,
    recipients,
    recipient_details: recipientDetails,
    status,
    last_date: createdAt || now,
    correspondence_count: 1,
    created_at: createdAt || now,
    updated_at: createdAt || now,
  };
};

const queryMatchesClient = (
  queryKey: readonly unknown[],
  clientIds: number[] = [],
  fallbackClientId?: number,
) => {
  const queryFilters = queryKey[1] as CommunicationThreadFilters | undefined;
  const clientKey = queryFilters?.client_id;
  if (!clientKey) return true;
  return clientIds.includes(clientKey) || fallbackClientId === clientKey;
};

const prependThreadToCache = (
  current: InfiniteCommunicationsData | undefined,
  optimisticThread: CommunicationThread,
) => {
  if (!isInfiniteCommunicationsData(current)) {
    return {
      pages: [
        {
          total: 1,
          page: 1,
          page_size: 20,
          data: [optimisticThread],
        },
      ],
      pageParams: [1],
    } satisfies InfiniteCommunicationsData;
  }

  return {
    ...current,
    pages: current.pages.map((page, index) =>
      index === 0
        ? {
            ...page,
            total: page.total + 1,
            data: [optimisticThread, ...page.data],
          }
        : page,
    ),
  };
};

const removeThreadFromCache = (
  current: InfiniteCommunicationsData | undefined,
  temporaryThreadId: number,
) => {
  if (!isInfiniteCommunicationsData(current)) return current;

  return {
    ...current,
    pages: current.pages.map(page => {
      const filtered = page.data.filter(thread => thread.id !== temporaryThreadId);
      return {
        ...page,
        total: Math.max(page.total - (filtered.length === page.data.length ? 0 : 1), 0),
        data: filtered,
      };
    }),
  };
};

const replaceTemporaryThreadInCache = (
  current: InfiniteCommunicationsData | undefined,
  temporaryThreadId: number,
  realThread: CommunicationThread,
) => {
  if (!isInfiniteCommunicationsData(current)) return current;

  return {
    ...current,
    pages: current.pages.map(page => ({
      ...page,
      data: page.data.map(thread => (thread.id === temporaryThreadId ? realThread : thread)),
    })),
  };
};

const updateThreadStatusInCache = (
  current: InfiniteCommunicationsData | undefined,
  threadId: number,
  threadType: CommunicationThreadType,
  status: CommunicationStatus,
) => {
  if (!isInfiniteCommunicationsData(current)) return current;

  return {
    ...current,
    pages: current.pages.map(page => ({
      ...page,
      data: page.data.map(thread =>
        thread.thread_id === threadId && thread.thread_type === threadType
          ? { ...thread, status }
          : thread,
      ),
    })),
  };
};

export const useCommunicationsQuery = (filters?: CommunicationThreadFilters) => {
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<PaginatedResponse<CommunicationThread>>({
    queryKey: [COMMUNICATIONS_QUERY_KEY, filters || {}, businessId],
    staleTime: 1000 * 60 * 5,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.v1.getCommunicationThreadsV1CommunicationsGet({
        page: typeof pageParam === 'number' ? pageParam : 1,
        page_size: 20,
        ...(filters || {}),
      } as any);
      return response.data;
    },
    keepPreviousData: true,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      return totalPage > page ? page + 1 : undefined;
    },
  });
};

export const useCommunicationMessagesQuery = (
  threadId?: number,
  threadType: CommunicationThreadType = GeneratedCommunicationThreadType.Communication,
) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetCommunicationMessagesResponse & { data: CommunicationMessage[] }>({
    queryKey: [COMMUNICATIONS_QUERY_KEY, 'messages', threadId, threadType, businessId],
    queryFn: async () => {
      if (!threadId) {
        throw new Error('threadId is required to fetch communication messages');
      }
      return fetchCommunicationMessages(threadId, threadType);
    },
    enabled: !!threadId,
    staleTime: 1000 * 30,
  });
};

export const useSendCommunicationMutation = () => {
  return useMutation({
    mutationFn: async (payload: SendCommunicationPayload) => {
      const requestPayload = { ...payload };
      delete requestPayload.optimistic_recipients;
      delete requestPayload.optimistic_recipient_details;
      const response = await withErrorHandling(
        api.v1.sendCommunicationV1CommunicationsSendPost(requestPayload),
        {
          loading: 'Sending email...',
          success: '',
        },
      );
      return response.data;
    },
    onMutate: async payload => {
      await queryClient.cancelQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });

      const optimisticThread = buildOptimisticThread({
        subject: payload.subject,
        preview: payload.content,
        recipients: payload.optimistic_recipients || payload.recipient_emails,
        recipientDetails: payload.optimistic_recipient_details,
        status: CommunicationStatus.Pending,
        communicationOrigin: CommunicationOrigin.Manual,
        clientId: payload.client_id,
        clientIds: payload.client_id ? [payload.client_id] : undefined,
        correspondenceType: CorrespondenceType.Email,
        senderName: 'Compli One',
      });
      const previousQueries = queryClient.getQueriesData<InfiniteCommunicationsData>({
        queryKey: [COMMUNICATIONS_QUERY_KEY],
      });

      previousQueries.forEach(([queryKey, current]) => {
        const queryFilters = queryKey[1] as CommunicationThreadFilters | undefined;
        if (
          queryMatchesClient(
            queryKey,
            payload.client_id ? [payload.client_id] : [],
            payload.client_id,
          ) &&
          threadMatchesFilters(optimisticThread, queryFilters)
        ) {
          queryClient.setQueryData(queryKey, prependThreadToCache(current, optimisticThread));
        }
      });

      return {
        previousQueries,
        optimisticThreadId: optimisticThread.id,
        startedAt: Date.now(),
      };
    },
    onError: (_error, _payload, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (response, payload, context) => {
      if (!context?.optimisticThreadId) return;

      const communication = response.communication;
      const resolvedClientId = communication.client_id ?? payload.client_id;
      const completedThread: CommunicationThread = {
        id: communication.id,
        thread_id: communication.id,
        thread_type: GeneratedCommunicationThreadType.Communication,
        communication_origin: communication.communication_origin ?? CommunicationOrigin.Manual,
        correspondence_type: CorrespondenceType.Email,
        client_id: resolvedClientId,
        client_ids: resolvedClientId ? [resolvedClientId] : [],
        subject: communication.subject,
        preview: payload.content,
        sender_name: 'Compli One',
        sender_email: undefined,
        recipients: payload.optimistic_recipients || payload.recipient_emails,
        recipient_details: payload.optimistic_recipient_details,
        status: communication.status,
        last_date: communication.updated_at || communication.created_at || new Date().toISOString(),
        correspondence_count: 1,
        created_at: communication.created_at || new Date().toISOString(),
        updated_at:
          communication.updated_at || communication.created_at || new Date().toISOString(),
      };

      const applyCompletedThread = () => {
        const matchedQueries = queryClient.getQueriesData<InfiniteCommunicationsData>({
          queryKey: [COMMUNICATIONS_QUERY_KEY],
        });

        matchedQueries.forEach(([queryKey, current]) => {
          queryClient.setQueryData(
            queryKey,
            replaceTemporaryThreadInCache(current, context.optimisticThreadId, completedThread),
          );
        });

        toast.success('Email sent');
      };

      const elapsed = Date.now() - (context.startedAt || Date.now());
      if (elapsed < MIN_PENDING_VISIBILITY_MS) {
        window.setTimeout(applyCompletedThread, MIN_PENDING_VISIBILITY_MS - elapsed);
        return;
      }

      applyCompletedThread();
    },
    onSettled: (_response, _error, _payload, context) => {
      if (_error && context?.optimisticThreadId) {
        const matchedQueries = queryClient.getQueriesData<InfiniteCommunicationsData>({
          queryKey: [COMMUNICATIONS_QUERY_KEY],
        });

        matchedQueries.forEach(([queryKey, current]) => {
          queryClient.setQueryData(
            queryKey,
            removeThreadFromCache(current, context.optimisticThreadId),
          );
        });
      }

      if (!_error) {
        window.setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });
        }, SUCCESS_REFETCH_DELAY_MS);
        return;
      }

      queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });
    },
  });
};

export const useCreateCommunicationHistoryRecordMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateCommunicationHistoryRecordPayload) => {
      const toastId = toast.loading('Saving record...');
      const requestPayload = { ...payload };
      const selectedFiles = payload.files || [];
      try {
        delete requestPayload.optimistic_recipients;
        delete requestPayload.optimistic_recipient_details;
        delete requestPayload.files;
        const response = await api.v1.createCommunicationHistoryRecordV1CommunicationsHistoryPost({
          ...requestPayload,
          files: selectedFiles.map(fileToHistoryRecordInitItem),
        });

        let attachmentsIncomplete = false;
        if (selectedFiles.length && response.data.uploads?.length) {
          try {
            const storageItemIds = await uploadCommunicationHistoryRecordFiles(
              response.data.uploads,
              selectedFiles,
            );

            if (storageItemIds.length) {
              await api.v1.completeCommunicationHistoryRecordFilesV1CommunicationsHistoryCorrespondenceIdCompleteFilesPost(
                { correspondenceId: response.data.correspondence_id },
                { storage_item_ids: storageItemIds } as CompleteCommunicationHistoryRecordFilesBody,
              );
            }
          } catch (_uploadError) {
            attachmentsIncomplete = true;
            toast.error(getAttachmentPartialFailureMessage('saved'), {
              id: toastId,
            });
          }
        }

        if (!attachmentsIncomplete) {
          toast.dismiss(toastId);
        }
        return {
          data: response.data,
          attachments_incomplete: attachmentsIncomplete,
        } satisfies PersistedHistoryRecordResult<typeof response.data>;
      } catch (error) {
        toast.error(formatApiErrorMessage(error as AxiosError<CompliOneApiError>), {
          id: toastId,
        });
        throw error;
      }
    },
    onMutate: async payload => {
      await queryClient.cancelQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });

      const createdAt = payload.occurred_at || new Date().toISOString();
      const clientIds = payload.client_ids || [];
      const optimisticThread = buildOptimisticThread({
        subject: payload.subject,
        preview: payload.content,
        recipients: payload.optimistic_recipients || payload.recipient_emails,
        recipientDetails: payload.optimistic_recipient_details,
        status: CommunicationStatus.Pending,
        communicationOrigin: CommunicationOrigin.Manual,
        clientIds,
        clientId: clientIds.length === 1 ? clientIds[0] : undefined,
        correspondenceType: payload.correspondence_type,
        senderName: 'Compli One',
        createdAt,
      });
      const previousQueries = queryClient.getQueriesData<InfiniteCommunicationsData>({
        queryKey: [COMMUNICATIONS_QUERY_KEY],
      });

      previousQueries.forEach(([queryKey, current]) => {
        const queryFilters = queryKey[1] as CommunicationThreadFilters | undefined;
        if (
          queryMatchesClient(queryKey, clientIds) &&
          threadMatchesFilters(optimisticThread, queryFilters)
        ) {
          queryClient.setQueryData(queryKey, prependThreadToCache(current, optimisticThread));
        }
      });

      return {
        previousQueries,
        optimisticThreadId: optimisticThread.id,
        startedAt: Date.now(),
      };
    },
    onError: (_error, _payload, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (result, payload, context) => {
      if (!context?.optimisticThreadId) return;

      const communication = result.data.communication;
      const completedThread: CommunicationThread = {
        id: communication.id,
        thread_id: communication.id,
        thread_type: GeneratedCommunicationThreadType.Communication,
        communication_origin: communication.communication_origin ?? CommunicationOrigin.Manual,
        correspondence_type: payload.correspondence_type,
        client_id: communication.client_id,
        client_ids: payload.client_ids || [],
        subject: communication.subject,
        preview: payload.content,
        sender_name: 'Compli One',
        sender_email: undefined,
        recipients: payload.optimistic_recipients || payload.recipient_emails,
        recipient_details: payload.optimistic_recipient_details,
        status: communication.status,
        last_date:
          payload.occurred_at ||
          communication.updated_at ||
          communication.created_at ||
          new Date().toISOString(),
        correspondence_count: 1,
        created_at: payload.occurred_at || communication.created_at || new Date().toISOString(),
        updated_at:
          communication.updated_at || communication.created_at || new Date().toISOString(),
      };

      const applyCompletedThread = () => {
        const matchedQueries = queryClient.getQueriesData<InfiniteCommunicationsData>({
          queryKey: [COMMUNICATIONS_QUERY_KEY],
        });

        matchedQueries.forEach(([queryKey, current]) => {
          queryClient.setQueryData(
            queryKey,
            replaceTemporaryThreadInCache(current, context.optimisticThreadId, completedThread),
          );
        });

        if (!result.attachments_incomplete) {
          toast.success('Record saved');
        }
      };

      const elapsed = Date.now() - (context.startedAt || Date.now());
      if (elapsed < MIN_PENDING_VISIBILITY_MS) {
        window.setTimeout(applyCompletedThread, MIN_PENDING_VISIBILITY_MS - elapsed);
        return;
      }

      applyCompletedThread();
    },
    onSettled: (_response, _error, _payload, context) => {
      if (_error && context?.optimisticThreadId) {
        const matchedQueries = queryClient.getQueriesData<InfiniteCommunicationsData>({
          queryKey: [COMMUNICATIONS_QUERY_KEY],
        });

        matchedQueries.forEach(([queryKey, current]) => {
          queryClient.setQueryData(
            queryKey,
            removeThreadFromCache(current, context.optimisticThreadId),
          );
        });
      }

      queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });
    },
  });
};

export const useUpdateCommunicationRecordMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateCommunicationRecordPayload) => {
      const toastId = toast.loading('Saving changes...');
      const selectedFiles = payload.files || [];
      try {
        const { correspondence_id, ...requestPayload } = payload;
        const response =
          await api.v1.updateCommunicationRecordV1CommunicationsHistoryCorrespondenceIdPatch(
            { correspondenceId: correspondence_id },
            {
              ...requestPayload,
              files: selectedFiles.map(fileToHistoryRecordInitItem),
            },
          );

        let attachmentsIncomplete = false;
        if (selectedFiles.length && response.data.uploads?.length) {
          try {
            const storageItemIds = await uploadCommunicationHistoryRecordFiles(
              response.data.uploads,
              selectedFiles,
            );

            if (storageItemIds.length) {
              await api.v1.completeCommunicationHistoryRecordFilesV1CommunicationsHistoryCorrespondenceIdCompleteFilesPost(
                { correspondenceId: correspondence_id },
                { storage_item_ids: storageItemIds } as CompleteCommunicationHistoryRecordFilesBody,
              );
            }
          } catch (_uploadError) {
            attachmentsIncomplete = true;
            toast.error(getAttachmentPartialFailureMessage('updated'), {
              id: toastId,
            });
          }
        }

        if (!attachmentsIncomplete) {
          toast.success('Record updated', { id: toastId });
        }
        return {
          data: response.data,
          attachments_incomplete: attachmentsIncomplete,
        } satisfies PersistedHistoryRecordResult<typeof response.data>;
      } catch (error) {
        toast.error(formatApiErrorMessage(error as AxiosError<CompliOneApiError>), {
          id: toastId,
        });
        throw error;
      }
    },
    onMutate: async payload => {
      await queryClient.cancelQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });
      const previousQueries = queryClient.getQueriesData<InfiniteCommunicationsData>({
        queryKey: [COMMUNICATIONS_QUERY_KEY],
      });

      if (payload.optimistic_thread_id && payload.optimistic_thread_type) {
        previousQueries.forEach(([queryKey, current]) => {
          const optimisticThreadId = payload.optimistic_thread_id;
          const optimisticThreadType = payload.optimistic_thread_type;
          if (!optimisticThreadId || !optimisticThreadType) return;
          queryClient.setQueryData(
            queryKey,
            updateThreadStatusInCache(
              current,
              optimisticThreadId,
              optimisticThreadType,
              CommunicationStatus.Pending,
            ),
          );
        });
      }

      return { previousQueries };
    },
    onError: (_error, _payload, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY, 'messages'] });
    },
  });
};

export const useDeleteCommunicationRecordMutation = () => {
  return useMutation({
    mutationFn: async (payload: DeleteCommunicationRecordPayload) => {
      await withErrorHandling(
        api.v1.deleteCommunicationRecordV1CommunicationsHistoryCorrespondenceIdDelete({
          correspondenceId: payload.correspondence_id,
        }),
        {
          loading: 'Deleting record...',
          success: 'Record deleted',
        },
      );
    },
    onMutate: async payload => {
      await queryClient.cancelQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });
      const previousQueries = queryClient.getQueriesData<InfiniteCommunicationsData>({
        queryKey: [COMMUNICATIONS_QUERY_KEY],
      });

      if (payload.optimistic_thread_id && payload.optimistic_thread_type) {
        previousQueries.forEach(([queryKey, current]) => {
          const optimisticThreadId = payload.optimistic_thread_id;
          const optimisticThreadType = payload.optimistic_thread_type;
          if (!optimisticThreadId || !optimisticThreadType) return;
          queryClient.setQueryData(
            queryKey,
            updateThreadStatusInCache(
              current,
              optimisticThreadId,
              optimisticThreadType,
              CommunicationStatus.Pending,
            ),
          );
        });
      }

      return { previousQueries };
    },
    onError: (_error, _payload, context) => {
      context?.previousQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY, 'messages'] });
    },
  });
};

export const useReassignCommunicationThreadMutation = () => {
  return useMutation({
    mutationFn: async (payload: { threadId: number; clientId: number }) => {
      const requestPayload: ReassignCommunicationThreadClientRequest = {
        thread_id: payload.threadId,
        client_id: payload.clientId,
      };
      const response =
        await api.v1.reassignCommunicationThreadClientV1CommunicationsThreadIdClientPatch(
          { threadId: payload.threadId },
          requestPayload,
        );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMUNICATIONS_QUERY_KEY] });
    },
  });
};
